import {Col, Row, Table} from 'antd';
import {Trans} from '@lingui/macro';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import CustomPagination from "../CustomPagination";
import {withRouter} from "react-router";
import {BulkActionPanel} from "../BulkAction/BulkActionPanel";
import styled from 'styled-components';
import CustomizeColumnButton from "../Drawer/CustomizeColumns/CustomizeColumnButton";
import ResizableTitle from "./ResizableTitle";
import LocalStorageActions from "bukku/redux/LocalStorage/actions";
import {ColumnsHelper} from "../Columns/ColumnsHelper";
import BukkuTooltip from '../Tooltip';
import { StyledCol } from './Style';

const { setColumnWidth, setPageSize } = LocalStorageActions;

/**
 * Props expected:
 * - dataSource: Data[]
 * - Columns: Column[]
 *
 * OPTIONAL: Pagination and Sorting
 * - pagination
 *      ** Set {total: Int, current: Int, pageSize: Int} to define externally.
 *      ** Set true to enable pagination; will track internally.
 *      ** Set false to disable pagination [default]
 *
 * - onPageChange: Function (nextPage, pageSize) => null
 *      ** called when pagination is changed
 *      ** Not required when pagination is False
 * - onTableChange: Function (pagination, filters, sorter, extra) => null
 *      ** called when sorting is changed
 *      ** Only required when there are sorters
 *
 * OPTIONAL: CUSTOM COLUMNS
 * - storageKey: StorageKey or False
 *      ** set False to show all Columns
 *
 * OPTIONAL: BULK ACTIONS
 * - selection
 *      ** Set string[] to define externally. Enables checkbox.
 *      ** Set true to enable pagination; will track internally. Enable checkbox.
 *      ** Set false to disable checkbox [default]
 * - noCheckboxFn: Function (record) => Boolean
 *      ** this allows to hide checkbox for certain records
 * - disableCheckboxFn: Function (record) => Boolean
 *      ** this allows to disable checkbox for certain records
 *      ** relates to:
 *          - disableCheckboxTooltipTitle: String
 *              ** Tooltip title to show when checkbox is disabled
 * - renderBulkActionButtons: Array of Function
 *                  - (selectedIds: string[], setSelectedIds: Function(string[]) => Void) => BulkActionButton
 *      ** this function defines how to generate BulkActionButton (refer TransactionTable for example)
 * - onSelectionChange: Function (selectedIds: string[]) => null
 * - selectionIdsMustExist: boolean [default: true]
 *      ** Set false if the selectionIds can exist outside of the provided dataSource.
 *
 * OPTIONAL: Extra Buttons (Top Right Corner)
 * - renderExtraButtons: Array of React Components
 *
 * OPTIONAL: Others
 * - className: ClassName for Table
 * - expandedRowKeys
 * - standardWidth: Int (default = 140px)
 * - isProductTable: boolean, only passed in from Products index.js table
 */

const Styled = styled.div`
  th {
    line-height: 1.25;
  }
`;

class BukkuTable extends Component {
    static defaultProps = {
        selection: false,
        pagination: false,
        storageKey: false,
        renderBulkActionButtons: [],
        renderExtraButtons: [],
        selectionIdsMustExist: true,
        hideTopPagination: false,
        hideBottomPagination: false,
        hideTopBulkActions: false,
        hideBottomBulkActions: false,
        hideTopExtraActions: false,
        hideBottomExtraActions: false,
        showZeroSelected: false,
        hideCustomizeButton: false,
        disableSorting: false,
        hideSelectAll: false,
        loading: undefined,
        showHeader: true,
        defaultPageSize: 10,
        scrollY: undefined,
    };

    components = {
        header: {
            cell: ResizableTitle,
        },
    };

    constructor(props) {
        super(props);
        const { defaultPageSize, storageData } = this.props;

        this.state = {
            selectedIds: [],
            currentPage: 1, // used when pagination={true}
            pageSize: storageData.page_size || defaultPageSize, // used when pagination={true}
        };

        this.paginationRef = React.createRef();

        this.observer = null;
    }

    componentDidMount() {
        const {fixedPagination} = this.props;

        const handlers = document.querySelectorAll(
            ".react-resizable .react-resizable-handle",
        );
        handlers.forEach(handler => handler.addEventListener("click", () => false));

        // to check when sticky paginator header gets pinned (only for txn pages)
        if (fixedPagination) {
            this.observer = new IntersectionObserver(
                ([e]) => e.target.classList.toggle("pagination-header-style", e.intersectionRatio < 1),
                { threshold: [1] },
            );

            if (this.paginationRef.current)
                this.observer.observe(this.paginationRef.current);
        }
    }

    componentDidUpdate(prevProps) {
        const { location } = this.props;

        // Change of query string
        if (location.search !== prevProps.location.search)
            this.onSelectionChange([]);
    }

    componentWillUnmount() {
        const {fixedPagination} = this.props;

        if (fixedPagination && this.paginationRef.current)
            this.observer.unobserve(this.paginationRef.current);
    }

    getSelectedIds = () => {
        const { selection } = this.props;
        const { selectedIds } = this.state;

        if (Array.isArray(selection))
            return selection;

        return selectedIds;
    };

    onSelectionChange = newSelectedIds => {
        const { dataSource, noCheckboxFn, onSelectionChange, selectionIdsMustExist } = this.props;

        const flattenDataSource = dataSource.flatMap(account => {
            if (!account.children?.length)
                return [account];
            return [account, ...account.children];
        });

        const filteredSelectedRowKeys = newSelectedIds.filter(r => {
            const record = flattenDataSource.find(t => t.id === r);
            if (record)
                return !noCheckboxFn || !noCheckboxFn(record);
            return !selectionIdsMustExist;
        });

        const finalSelectedIds = filteredSelectedRowKeys.length === this.getSelectedIds().length ?
            [] :
            filteredSelectedRowKeys;

        this.setState({
            selectedIds: finalSelectedIds,
        });

        if (onSelectionChange)
            onSelectionChange(finalSelectedIds);
    };

    getColumns = () => {
        const { columns, disableSorting, isResizable, storageData, storageKey } = this.props;

        if (!storageKey)
            return columns;

        if (isResizable) {
            return ColumnsHelper
                .filterResizableColumns(columns, storageData, this.handleResize)
                .map(column => ({
                    ...column,
                    // If Disable Sorting?
                    sorter: disableSorting ? false : column.sorter,
                }));
        }

        return ColumnsHelper
            .filterColumns(columns, storageData)
            .map(column => ({
                ...column,
                // If Disable Sorting?
                sorter: disableSorting ? false : column.sorter,
            }));
    };

    getScroll = columns => {
        const { standardWidth, scrollY } = this.props;
        // const { view, columnsSelected } = this.props;

        // const maxColumnsList = {
        //     'DesktopView': 9,
        //     'TabView': 7,
        //     'MobileView': 5,
        // }
        // const maxColumns = maxColumnsList[view || 'DesktopView']
        // if (columnsSelected.length <= maxColumns)
        //     return {};

        let totalWidth = 0;
        columns.forEach(column => {
            totalWidth += column.width || (standardWidth || 140);
            // console.log(`Col Width: ${column.key} == ${column.width || standardWidth}`)
        });
        // console.log("TotalWidth:", totalWidth)

        const scrollYObj = scrollY ? { y: scrollY } : {};

        return { x: totalWidth, ...scrollYObj };
    };

    getDataSource = () => {
        const { pagination, dataSource } = this.props;
        if ((typeof pagination === 'object' && pagination !== null) || !pagination)
            return dataSource;

        // Pagination is managed internally without being set from parent
        // pagination={true}
        const { currentPage, pageSize } = this.state;
        const firstIdx = parseInt((currentPage - 1) * pageSize, 10);
        const lastIdx = firstIdx + pageSize;
        return dataSource.slice(firstIdx, Math.min(lastIdx, dataSource.length));
    };

    getPagination = () => {
        const { pagination, dataSource } = this.props;

        if (typeof pagination === 'object' && pagination !== null)
            return pagination;

        // Pagination is managed internally without being set from parent
        // pagination={true}
        const { currentPage, pageSize } = this.state;
        return {
            total: dataSource.length,
            current: currentPage,
            pageSize,
        };
    };

    onPageChange = (nextPage, pageSize) => {
        const { pagination, onPageChange, storageKey, setPageSize } = this.props;

        if (storageKey)
            setPageSize(storageKey, pageSize);

        if (typeof pagination === 'object' && pagination !== null)
            return onPageChange(nextPage, pageSize);

        // Pagination is managed internally without being set from parent
        // pagination={true}
        this.setState({ currentPage: nextPage, pageSize});
        return null;
    };

    onTableChange = (pagination, filters, sorter, extra) => {
        const { onTableChange, storageKey } = this.props;

        if (onTableChange)
            onTableChange(pagination, filters, sorter, extra, storageKey);
    };

    // Deals only with setState => redux => localStorage => getColumns => columnsSelected
    // (width is the last index)
    handleResize = columnKey => (e, { size }) => {
        const { storageKey, setColumnWidth, isProductTable } = this.props;

        e.stopImmediatePropagation();
        // allow cols in prodcut table to have a bigger max width
        // allow 'name' and 'group_names' col in product table to have a bigger max width
        const maxWidth = (isProductTable && ['name', 'group_names'].includes(columnKey)) ? 500 : 300;
        const finalWidth = (size.width > maxWidth) ? maxWidth : size.width;

        setColumnWidth(storageKey, columnKey, finalWidth);
    };

    renderActionRow = isTop => {
        // transaction tables have fixed pagination header (the actions + pagination row)
        const {fixedPagination} = this.props;

        const leftContents = this.getLeftActions(isTop);
        const rightContents = this.getRightActions(isTop);

        const contents = [];
        if (leftContents.length) {
            contents.push(
                <Col key="bulk-actions">
                    <Row justify="start" gutter={[8, 4]} align="middle" style={{ marginBottom: -2}}>
                        {leftContents}
                    </Row>
                </Col>,
            );
        }

        if (rightContents.length) {
            contents.push(
                <Col flex={1} key="pagination">
                    <Row justify="end" gutter={[8, 4]} align="middle" style={{width: '100%'}}>
                        {rightContents}
                    </Row>
                </Col>,
            );
        }

        if (contents.length) {
            return fixedPagination && isTop ?
                (
                    <StyledCol
                        className="pagination-header-style"
                        span={24}
                        ref={this.paginationRef}
                    >
                        <Row align="middle" style={{padding: "8px 0px"}}>
                            { contents }
                        </Row>
                    </StyledCol>
                ) :
                (
                    <Col span={24}>
                        <Row align="middle">{ contents }</Row>
                    </Col>
                );
        }
        return null;
    };

    // Contains
    // - BulkActions (please provide <renderBulkActionButtons>)
    getLeftActions = isTop => {
        const {
            selection,
            renderBulkActionButtons,
            hideTopBulkActions,
            hideBottomBulkActions,
            showZeroSelected,
        } = this.props;
        const isBottom = !isTop;

        const leftActions = [];

        // Bulk Actions
        const showBulkAction = renderBulkActionButtons.length && selection !== false;
        if (showBulkAction) {
            const showTop = isTop && !hideTopBulkActions;
            const showBottom = isBottom && !hideBottomBulkActions;

            if (showTop || showBottom) {
                leftActions.push(
                    <Col key="bulk-action-panel">
                        <BulkActionPanel
                            renderBulkActionButtons={renderBulkActionButtons}
                            setSelectedIds={this.onSelectionChange}
                            selectedIds={this.getSelectedIds()}
                            showZeroSelected={showZeroSelected}
                        />
                    </Col>,
                );
            }
        }

        return leftActions;
    };

    // Contains
    // - Paginator,
    // - Built-in Customized Column Button (please provide <storageKey>)
    // - Export Buttons (please provide <renderExtraButtons>)
    getRightActions = isTop => {
        const {
            selection,
            pagination,
            storageKey,
            dataSource,
            hideTopPagination,
            hideBottomPagination,
            hideCustomizeButton,
            renderExtraButtons,
            hideTopExtraActions,
            hideBottomExtraActions,
            customColumnProps,
            customFieldColProps,
        } = this.props;
        const isBottom = !isTop;

        const rightActions = [];

        if (pagination) {
            const showTop = isTop && !hideTopPagination &&
            (selection !== false || (pagination.total || dataSource.length) > 10 || hideBottomPagination);
            const showBottom = isBottom && !hideBottomPagination;

            if (showTop || showBottom) {
                rightActions.push(
                    <Col key="paginator">
                        <CustomPagination
                            paginationData={this.getPagination()}
                            onPageChange={this.onPageChange || (() => {})}
                        />
                    </Col>,
                );
            }
        }

        const showCustomizeColumn = storageKey && !hideCustomizeButton;
        if (showCustomizeColumn) {
            rightActions.push(
                <Col key="customise-columns">
                    <BukkuTooltip title={<Trans>Customise Column</Trans>}>
                        <CustomizeColumnButton
                            storageKey={storageKey}
                            columnProps={customColumnProps}
                            customFieldColProps={customFieldColProps}
                        />
                    </BukkuTooltip>
                </Col>,
            );
        }

        const showExtraButtons = renderExtraButtons.length;
        if (showExtraButtons) {
            const showTop = isTop && !hideTopExtraActions;
            const showBottom = isBottom && !hideBottomExtraActions;

            if (showTop || showBottom) {
                rightActions.push(
                    <Col key="extra-buttons">
                        <Row gutter={[6, 0]} align="middle">
                            {/*eslint-disable-next-line react/no-array-index-key*/}
                            { renderExtraButtons.map((button, idx) => <Col key={idx}>{button}</Col>)}
                        </Row>
                    </Col>,
                );
            }
        }

        return rightActions;
    };

    render() {
        const {
            loading,
            isPulling,
            rowKey,
            selection,
            noCheckboxFn,
            className,
            expandedRowKeys,
            rowClassName,
            hideSelectAll,
            showHeader,
            onRow,
            summary,
            expandable,
            disableCheckboxFn,
            disableCheckboxTooltipTitle,
        } = this.props;

        const columns = this.getColumns(); // correct width

        // Handles multirow selection
        const rowSelection = {
            selectedRowKeys: this.getSelectedIds(),
            getCheckboxProps: record => ({
                style: noCheckboxFn && noCheckboxFn(record) ? {display: 'none'} : null,
                disabled: !!(disableCheckboxFn && disableCheckboxFn(record)),
            }),
            onChange: this.onSelectionChange,
            hideSelectAll,
            renderCell: (checked, record, index, originNode) => {
                // If checkbox is disabled and a tooltip title is supplied, render it
                if (disableCheckboxFn && disableCheckboxFn(record) && disableCheckboxTooltipTitle) {
                    return (
                        <BukkuTooltip title={disableCheckboxTooltipTitle}>
                            {originNode}
                        </BukkuTooltip>
                    );
                }
                return originNode; // the original checkbox cell
            },
        };

        return (
            <Styled>
                <Row gutter={[0, 8]}>
                    { this.renderActionRow(true) }
                    <Col span={24}>
                        <Table
                            rowKey={rowKey || "id"}
                            size="small"
                            className={className}
                            expandedRowKeys={expandedRowKeys}
                            components={this.components}
                            columns={columns}
                            showHeader={showHeader}
                            dataSource={this.getDataSource()}
                            onChange={this.onTableChange}
                            pagination={false}
                            rowSelection={selection !== false ? {
                                type: 'checkbox',
                                preserveSelectedRowKeys: true,
                                ...rowSelection,
                            } : null}
                            scroll={this.getScroll(columns)}
                            showSorterTooltip={false}
                            loading={loading === undefined ? isPulling : loading}
                            rowClassName={rowClassName}
                            onRow={onRow}
                            summary={summary}
                            expandable={expandable}
                        />
                    </Col>
                    { this.renderActionRow(false) }
                </Row>
            </Styled>
        );
    }
}

export default withRouter(connect((state, props) => ({
    isPulling: state.App.isPulling,
    fields: state.List.fields,
    storageData: state.LocalStorage[props.storageKey] || {},
}), {
    setColumnWidth,
    setPageSize,
})(BukkuTable));
